/**
 * Composes authenticated web routes and the shared application shell.
 * Routes follow server-issued capabilities; business rules and authorization stay in services.
 */
import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { canManagePluginStore } from '../../shared/domain/plugin-access.ts';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { Sidebar } from './components/Sidebar.tsx';
import { adminHomePath } from './lib/admin-home.ts';
import { api, clearSession, hasSession, setUnauthorizedHandler, type AuthUser } from './lib/api.ts';
import { ChangePassword } from './routes/ChangePassword.tsx';
import { Login } from './routes/Login.tsx';
import { SsoAuth } from './routes/SsoAuth.tsx';

/**
 * 路由级代码分割。
 *
 * 看板与指标详情共用图表库（recharts 占了大半个 bundle），
 * 而待决队列与字典根本用不到它 —— 不分割的话，打开待决队列也要先下载图表库。
 */
const DshPluginsPage = lazy(() => import('./routes/extensions/DshPlugins.tsx').then(m => ({ default: m.DshPluginsPage })));
const EngineeringPacksPage = lazy(() => import('./routes/extensions/EngineeringPacks.tsx').then(m => ({default:m.EngineeringPacksPage})));
const Dashboard = lazy(() => import('./routes/Dashboard.tsx').then((m) => ({ default: m.Dashboard })));
const MetricDetail = lazy(() =>
  import('./routes/MetricDetail.tsx').then((m) => ({ default: m.MetricDetail })),
);
const Decisions = lazy(() => import('./routes/Decisions.tsx').then((m) => ({ default: m.Decisions })));
const Dictionary = lazy(() => import('./routes/Dictionary.tsx').then((m) => ({ default: m.Dictionary })));
const Admin = lazy(() => import('./routes/Admin.tsx').then((m) => ({ default: m.Admin })));
const Attribution = lazy(() =>
  import('./routes/Attribution.tsx').then((m) => ({ default: m.Attribution })),
);
const Rules = lazy(() => import('./routes/Rules.tsx').then((m) => ({ default: m.Rules })));
const Relays = lazy(() => import('./routes/relays/Relays.tsx').then((m) => ({ default: m.Relays })));
const SettingsRoutes = lazy(() =>
  import('./routes/settings/SettingsRouter.tsx').then((m) => ({ default: m.SettingsRoutes })),
);
const HypothesisDetail = lazy(() =>
  import('./routes/HypothesisDetail.tsx').then((m) => ({ default: m.HypothesisDetail })),
);
const Tasks = lazy(() => import('./routes/Tasks.tsx').then((m) => ({ default: m.Tasks })));
const MyModels = lazy(() => import('./routes/models/MyModels.tsx').then(m => ({ default: m.MyModels })));
const Sessions = lazy(() => import('./routes/Sessions.tsx').then((m) => ({ default: m.Sessions })));
const McpKeys = lazy(() => import('./routes/McpKeys.tsx').then((m) => ({ default: m.McpKeys })));
const Manual = lazy(() => import('./routes/Manual.tsx').then((m) => ({ default: m.Manual })));
const GitlabAudit = lazy(() =>
  import('./routes/GitlabAudit.tsx').then((m) => ({ default: m.GitlabAudit })),
);
const Usage = lazy(() => import('./routes/Usage.tsx').then((m) => ({ default: m.Usage })));
const AdminUsage = lazy(() => import('./routes/usage/AdminUsage.tsx').then((m) => ({ default: m.AdminUsage })));
const SandboxAnalysis = lazy(() => import('./routes/SandboxAnalysis.tsx').then((m) => ({ default: m.SandboxAnalysis })));

/* 版本更新提示。不自动刷新 —— 打断正在进行的操作比跑旧版更糟，选择权给人。 */
function UpdateBanner({ show }: { show: boolean }) {
  if (!show) return null;
  return (
    <div className="update-banner">
      <span>系统已更新到新版本，当前页面还在旧版 —— 部分功能可能缺失。</span>
      <button type="button" onClick={() => window.location.reload()}>
        立即刷新
      </button>
    </div>
  );
}

export function App() {
  const location = useLocation();
  const [authed, setAuthed] = useState(hasSession());
  const [me, setMe] = useState<AuthUser | null>(null);
  const [needsPwChange, setNeedsPwChange] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [outdated, setOutdated] = useState(false);

  // 版本检测：SPA 长驻的旧 bundle 是发版的盲区 —— 用户不刷新就永远跑旧界面，
  // 还以为是功能缺失。index.html 是 no-cache 的，轮询它对比 bundle 文件名，
  // 发现服务端有更新版本就提示刷新（不自动刷新，避免打断正在进行的操作）。
  useEffect(() => {
    if (import.meta.env.DEV) return;
    const check = (): void => {
      void fetch('/', { cache: 'no-store' })
        .then((r) => r.text())
        .then((html) => {
          const latest = /assets\/index-([A-Za-z0-9_-]+)\.js/.exec(html)?.[1];
          const current = /index-([A-Za-z0-9_-]+)\.js/.exec(
            document.querySelector('script[src*="assets/index-"]')?.getAttribute('src') ?? '',
          )?.[1];
          if (latest !== undefined && current !== undefined && latest !== current) {
            setOutdated(true);
          }
        })
        .catch(() => {});
    };
    void check();
    const t = window.setInterval(check, 5 * 60 * 1000);
    return () => window.clearInterval(t);
  }, []);

  // 任何接口返回 401（令牌过期/被停用）时，清会话回到登录页 ——
  // 让过期的 token 表现为「请重新登录」，而不是每个页面各自报错
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession();
      setMe(null);
      setNeedsPwChange(false);
      setAuthed(false);
    });
  }, []);

  // me 是否已取到（失败也算「已取到」）。**权限路由渲染的前提**：
  // perms 来自 me，me 未到时路由表里没有 /rules、/settings 这些条件路由，
  // 兜底的 Navigate to="/" 会把刷新中的地址弹回首页 —— 表现为
  // 「刷新后一级菜单选择失效」。所以 me 到位前不渲染路由表。
  const [meLoaded, setMeLoaded] = useState(false);

  // 登录后取一次当前用户，给侧栏底部显示；
  // 发现会话还挂着 mustChangePassword（升级前发的旧 token）时全屏拦到改密页
  const loadMe = useCallback((): void => {
    api
      .me()
      .then((r) => {
        setMe(r.user);
        setNeedsPwChange(r.user?.mustChangePassword === true);
      })
      .catch(() => {
        // 401 已由统一回调处理；静态 token（无用户语义）则不显示
      })
      .finally(() => {
        setMeLoaded(true);
      });
  }, []);

  useEffect(() => {
    if (!authed) return;
    loadMe();
  }, [authed, loadMe]);

  const signOut = (): void => {
    void api.logout().catch(() => {});
    clearSession();
    setMe(null);
    setNeedsPwChange(false);
    setAuthed(false);
  };

  // 导航与页面的权限显隐。服务端仍是真正的门（接口挂 permission 点），
  // 这里只决定「看不看得见入口」
  const perms = me?.permissions ?? [];
  const canAttribution = perms.includes('attribution:read');
  const canRules = perms.includes('rule:manage');
  const isAdmin = me?.role === 'admin';
  const adminHome = isAdmin ? adminHomePath(perms, me?.role) : null;

  // Desktop handoff must run even when this browser already has a Web session;
  // otherwise an authenticated visitor never reaches the claim flow.
  if (window.location.pathname.startsWith('/sso')) {
    return (
      <>
        <UpdateBanner show={outdated} />
        <SsoAuth
          onAuthed={() => {
            setMeLoaded(false);
            setAuthed(true);
          }}
        />
      </>
    );
  }

  if (!authed) {
    return (
      <>
        <UpdateBanner show={outdated} />
        <Login
          onAuthed={() => {
            setMeLoaded(false);
            setAuthed(true);
          }}
        />
      </>
    );
  }

  if (needsPwChange) {
    return (
      <>
        <UpdateBanner show={outdated} />
        <ChangePassword
          username={me?.username ?? ''}
          onDone={() => {
            // 改完重取 me：must_change_password 已被服务端清零，正常进入系统
            loadMe();
          }}
        />
      </>
    );
  }

  // 权限未就绪：渲染路由表会把带权限条件的地址误弹回首页（见 meLoaded 注释）
  if (!meLoaded) {
    return (
      <>
        <UpdateBanner show={outdated} />
        <div className="empty" style={{ minHeight: '60vh' }}>
          读取中…
        </div>
      </>
    );
  }

  if (showPw) {
    return (
      <>
        <UpdateBanner show={outdated} />
        <ChangePassword
          heading="修改密码"
          submitLabel="确认修改"
          username={me?.username ?? ''}
          onDone={() => setShowPw(false)}
          onCancel={() => setShowPw(false)}
        />
      </>
    );
  }

  if (me && /^\/manual\/?$/.test(location.pathname)) {
    return (
      <>
        <UpdateBanner show={outdated} />
        <Suspense fallback={<div className="empty">读取中…</div>}>
          <Manual />
        </Suspense>
      </>
    );
  }

  return (
    <div className="layout">
      <UpdateBanner show={outdated} />
      <Sidebar
        perms={perms}
        me={me}
        onSignOut={signOut}
        onPassword={() => setShowPw(true)}
        onSessions={() => window.location.assign('/sessions')}
      />
      <main className="main">
        <Suspense fallback={<div className="empty">读取中…</div>}>
          <Routes>
            {isAdmin && perms.includes('config:manage') && <Route path="/sandbox" element={<SandboxAnalysis />} />}
            <Route path="/" element={isAdmin
              ? adminHome !== null
                ? <Navigate to={adminHome} replace />
                : <div className="page"><header className="pagehead"><div><h1>管理</h1><p>当前账号尚无可用的管理权限，请联系管理员配置。</p></div></header></div>
              : <Dashboard canReadMetrics={perms.includes('metrics:read')} />} />
            {!isAdmin && <>
              <Route path="/metric/:id" element={<MetricDetail />} />
              <Route
                path="/decisions"
                element={<Decisions canDecide={perms.includes('decision:decide')} />}
              />
              <Route path="/dictionary" element={<Dictionary />} />
            </>}
            {!isAdmin && canAttribution && (
              <>
                <Route path="/attribution" element={<Attribution />} />
                <Route path="/hypothesis/:id" element={<HypothesisDetail />} />
                <Route path="/tasks" element={<Tasks />} />
              </>
            )}
            {canRules && <Route path="/rules" element={<Rules meUsername={me?.username ?? ''} />} />}
            {perms.includes('relay:read') && <Route path="/relays" element={<Relays permissions={perms} />} />}
            {(perms.includes('config:manage') || canManagePluginStore(perms, me?.role)) && (
              <Route path="/settings/*" element={<SettingsRoutes permissions={perms} role={me?.role} />} />
            )}
            {canManagePluginStore(perms, me?.role) && <>
              <Route path="/extensions" element={<Navigate to="/extensions/dsh" replace />} />
              <Route path="/extensions/dsh" element={<DshPluginsPage />} />
              <Route path="/extensions/plugins" element={<Navigate to="/extensions/dsh" replace />} />
              <Route path="/extensions/skills" element={<EngineeringPacksPage />} />
            </>}
            <Route path="/admin" element={<Admin me={me} />} />
            <Route path="/sessions" element={<Sessions />} />
            {me && <Route path="/mcp-keys" element={<McpKeys key={me.id} ownerId={me.id} />} />}
            {me?.role !== 'admin' && <Route path="/models" element={<MyModels key={me?.username} owner={me?.username ?? ''} />} />}
            {(me?.role !== 'admin' || perms.includes('gitlab:audit')) && <Route path="/gitlab" element={<GitlabAudit canAudit={me?.role === 'admin' && perms.includes('gitlab:audit')} isAdmin={me?.role === 'admin'} userId={me?.id} />} />}
            {perms.includes('usage:read') && (
              <Route path="/usage" element={me?.role === 'admin' ? <AdminUsage canConfigure={perms.includes('config:manage')} /> : <Usage />} />
            )}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </main>
    </div>
  );
}
